import { styled } from "simplydo/react/styled";
import { Button, Checkbox, Icon, Modal, Table } from "simplydo/react/ui";

import { DndContext } from "components/lib/dnd/DndContext";
import { Sortable } from "components/lib/dnd/Sortable";
import { moveWithId } from "components/lib/dnd/dndHelpers";

import type { Column } from "./types";

const ReorderableRow = styled.div`
  user-select: none;
  padding: 10px 0;
  margin: 0 0 5px 0;
  border-radius: 5px;
  cursor: pointer;
`;

type TableSettingsProps<ItemType extends Record<string, any>> = {
  open: boolean;
  onClose: () => void;
  columns: Column<ItemType>[];
  updateColumns: (newColumns: Column<ItemType>[] | null) => void;
};

const TableSettings = <ItemType extends Record<string, any>>({
  open,
  onClose,
  columns,
  updateColumns,
}: TableSettingsProps<ItemType>) => {
  const updateColumn = (key: string, newColumn: Column<ItemType>) => {
    const index = columns.findIndex((col) => col.key === key);
    if (index === -1) return;

    const newColumns = [...columns];
    newColumns[index] = newColumn;
    updateColumns(newColumns);
  };

  return (
    <DndContext
      onDragEnd={(event) => {
        updateColumns(
          moveWithId("key", columns, event).map((col, i) => ({
            ...col,
            order: i,
          })),
        );
      }}
    >
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={open}
        closeOnDimmerClick
        onClose={onClose}
      >
        <Modal.Header>Update table</Modal.Header>
        <Modal.Content scrolling>
          <h3>Selected columns</h3>
          <p>
            Table columns can be modified here. Use the toggle to only display
            information that is relevant to you. Table columns can also be
            reordered via drag and drop.
          </p>
          <p>These settings will be synchronised between devices.</p>
          <div style={{ padding: 10 }}>
            <Table compact="very">
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Columns</Table.HeaderCell>
                  <Table.HeaderCell collapsing></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              {columns.map((column, index) => (
                <Sortable
                  key={column.key}
                  index={index}
                  id={column.key}
                  attachToChild
                >
                  <Table.Row>
                    <Table.Cell>
                      <Checkbox
                        toggle
                        checked={column.enabled}
                        onChange={(_e, check) => {
                          if (
                            (check.checked && !column.enabled) ||
                            (!check.checked && column.enabled)
                          ) {
                            updateColumn(column.key as string, {
                              ...column,
                              enabled: !!check.checked,
                            });
                          }
                        }}
                        label={column.settingName ?? column.name}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Icon
                        name="bars"
                        style={{ marginLeft: 10, marginRight: 10 }}
                      />
                    </Table.Cell>
                  </Table.Row>
                </Sortable>
              ))}
            </Table>
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => updateColumns(null)}>Reset to default</Button>
          <Button
            content="Done"
            labelPosition="right"
            icon="checkmark"
            onClick={onClose}
          />
        </Modal.Actions>
      </Modal>
    </DndContext>
  );
};

export default TableSettings;
